import {
  Avatar,
  Box,
  Card,
  CardHeader,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
} from '@mui/material';
import {
  Brightness4,
  LightMode,
  Logout,
  ViewSidebar,
} from '@mui/icons-material';
import { ReactElement, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMuiSwitchableTheme } from '../../theme';

type BaseDropdownItem = {
  label: string;
  icon: ReactElement;
};

type FunctionDropdownItem = BaseDropdownItem & {
  onClick: () => void;
};

type LinkDropdownItem = BaseDropdownItem & {
  href: string;
};

type DropDownItem = FunctionDropdownItem | LinkDropdownItem;

export interface AuthProps<TUser = unknown> {
  user: TUser;
  logoutFn: () => Promise<void>;
  vendorName: string;
  dropdownItems?: DropDownItem[];
}

export function Auth<
  TUser extends { first_name?: string; picture_url?: string | null },
>({ user, logoutFn, vendorName, dropdownItems = [] }: AuthProps<TUser>) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const {
    navMode,
    navCollapsed,
    navWidth,
    setMuiTheme,
    themeMode,
    setNavMode,
    isTablet,
  } = useMuiSwitchableTheme();

  const collapsed = useMemo(
    () => navMode === 'left' && navCollapsed,
    [navMode, navCollapsed],
  );

  return (
    <Box>
      <Card
        onClick={() => setOpen(true)}
        sx={{
          cursor: 'pointer',
          backgroundColor: 'transparent',
          backdropFilter: 'brightness(1.1)',
          color: 'inherit',
          maxHeight: 56,
        }}
        ref={ref}
      >
        <CardHeader
          avatar={
            <Avatar
              src={user.picture_url ?? undefined}
              alt={user.first_name ?? 'User Avatar'}
              sx={{ width: 31, height: 31 }}
            />
          }
          sx={{
            p: 1,
            transition: 'padding 0.3s ease',
          }}
          title={`Welcome, ${user.first_name}`}
          subheader={vendorName}
          slotProps={{
            subheader: {
              color: 'inherit',
              opacity: 0.8,
              sx: {
                display: collapsed ? 'none' : 'flex',
              },
            },
            title: {
              sx: {
                display: collapsed ? 'none' : 'flex',
              },
            },
          }}
        />
      </Card>
      <Menu
        id="basic-menu"
        anchorEl={ref.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={
          navMode === 'top'
            ? {
                vertical: 'bottom',
                horizontal: 'left',
              }
            : {
                vertical: 'top',
                horizontal: 'left',
              }
        }
        keepMounted
        transformOrigin={
          navMode === 'top'
            ? {
                vertical: 'top',
                horizontal: 'left',
              }
            : {
                vertical: 'bottom',
                horizontal: 'left',
              }
        }
        sx={{
          color: 'inherit',
        }}
        slotProps={{
          paper: {
            sx: {
              width: navCollapsed ? 'auto' : navWidth,
              backgroundColor: 'primary.light',
              color: 'primary.contrastText',
              mt: navMode === 'top' ? 1 : 0,
              mb: navMode === 'left' ? 2 : 0,
            },
          },
        }}
      >
        {dropdownItems.map((item) => {
          const isLinkItem = 'href' in item;
          const itemKey = isLinkItem
            ? `link-${(item as LinkDropdownItem).href}`
            : `action-${item.label}`;

          if (isLinkItem) {
            return (
              <MenuItem
                key={itemKey}
                component={Link}
                to={(item as LinkDropdownItem).href}
                onClick={() => setOpen(false)}
                sx={{ textDecoration: 'none', color: 'inherit' }}
              >
                <ListItemIcon sx={{ color: 'primary.contrastText' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText>{item.label}</ListItemText>
              </MenuItem>
            );
          }

          return (
            <MenuItem
              key={itemKey}
              onClick={() => {
                (item as FunctionDropdownItem).onClick();
                setOpen(false);
              }}
            >
              <ListItemIcon sx={{ color: 'primary.contrastText' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          );
        })}
        <Divider sx={{ my: 1, mx: 0.5, borderColor: 'primary.main' }} />
        <MenuItem
          onClick={() => setMuiTheme(themeMode === 'light' ? 'dark' : 'light')}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              width: 1,
            }}
          >
            <Brightness4 fontSize="small" />
            <Switch
              checked={themeMode === 'light'}
              color="success"
              onChange={(evt) => {
                setMuiTheme(evt.target.checked ? 'light' : 'dark');
                evt.stopPropagation();
              }}
            />
            <LightMode fontSize="small" />
          </Stack>
        </MenuItem>

        {!isTablet && (
          <MenuItem
            onClick={() => {
              setNavMode(navMode === 'left' ? 'top' : 'left');
              setOpen(false);
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                width: 1,
              }}
            >
              <ViewSidebar
                fontSize="small"
                sx={{ transform: 'rotate(180deg)' }}
              />
              <Switch
                color="success"
                checked={navMode === 'top'}
                onChange={(evt) => {
                  setNavMode(evt.target.checked ? 'top' : 'left');
                  setOpen(false);
                  evt.stopPropagation();
                }}
              />
              <ViewSidebar
                fontSize="small"
                sx={{ transform: 'rotate(270deg)' }}
              />
            </Stack>
          </MenuItem>
        )}
        <Divider sx={{ my: 1, mx: 0.5, borderColor: 'primary.main' }} />
        <MenuItem
          onClick={() => {
            logoutFn();
            setOpen(false);
          }}
        >
          <ListItemIcon sx={{ color: 'primary.contrastText' }}>
            <Logout />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
